import { computed, signal } from '@preact/signals';
import { selectedIds } from './selection';

// Equipment slot types - Rimworld-style apparel layers
export const EquipmentSlot = {
  // Weapons
  PRIMARY: 'primary',
  // Apparel layers
  HEADGEAR: 'headgear',
  TORSO: 'torso',
  LEGS: 'legs',
  // Utility
  UTILITY: 'utility',
} as const;

export type EquipmentSlotType = (typeof EquipmentSlot)[keyof typeof EquipmentSlot];

// Item rarity
export const ItemRarity = {
  COMMON: 'common',
  UNCOMMON: 'uncommon',
  RARE: 'rare',
  EPIC: 'epic',
  LEGENDARY: 'legendary',
} as const;

export type ItemRarityType = (typeof ItemRarity)[keyof typeof ItemRarity];

// Item definition
export interface ItemData {
  readonly id: number;
  readonly name: string;
  readonly icon: string;
  readonly rarity: ItemRarityType;
  readonly stackable: boolean;
  readonly quantity: number;
  readonly slot?: EquipmentSlotType;
  readonly stats?: {
    readonly attack?: number;
    readonly strength?: number;
    readonly defense?: number;
    readonly magic?: number;
    readonly ranged?: number;
  };
}

// Equipment state for a pawn - Rimworld-style
export interface PawnEquipment {
  readonly [EquipmentSlot.PRIMARY]: ItemData | null;
  readonly [EquipmentSlot.HEADGEAR]: ItemData | null;
  readonly [EquipmentSlot.TORSO]: ItemData | null;
  readonly [EquipmentSlot.LEGS]: ItemData | null;
  readonly [EquipmentSlot.UTILITY]: ItemData | null;
}

// Combat stance types - how the pawn fights
export const CombatStance = {
  BALANCED: 'balanced',
  AGGRESSIVE: 'aggressive',
  CAUTIOUS: 'cautious',
} as const;

export type CombatStanceType = (typeof CombatStance)[keyof typeof CombatStance];

// Combat stance info
export interface CombatStanceInfo {
  readonly name: string;
  readonly description: string;
}

export const COMBAT_STANCE_INFO: Record<CombatStanceType, CombatStanceInfo> = {
  [CombatStance.BALANCED]: {
    name: 'Balanced',
    description: 'Standard combat approach',
  },
  [CombatStance.AGGRESSIVE]: {
    name: 'Aggressive',
    description: 'More damage, less defense',
  },
  [CombatStance.CAUTIOUS]: {
    name: 'Cautious',
    description: 'Prioritize survival',
  },
};

// Inventory slot count
export const INVENTORY_SIZE = 32;

// State
export const pawnEquipment = signal<Map<number, PawnEquipment>>(new Map());
export const pawnInventory = signal<Map<number, (ItemData | null)[]>>(new Map());
export const pawnCombatStance = signal<Map<number, CombatStanceType>>(new Map());

// Create empty equipment
const createEmptyEquipment = (): PawnEquipment => ({
  [EquipmentSlot.PRIMARY]: null,
  [EquipmentSlot.HEADGEAR]: null,
  [EquipmentSlot.TORSO]: null,
  [EquipmentSlot.LEGS]: null,
  [EquipmentSlot.UTILITY]: null,
});

// Create empty inventory
const createEmptyInventory = (): (ItemData | null)[] => {
  return Array(INVENTORY_SIZE).fill(null);
};

// Get equipment for selected pawn
export const selectedPawnEquipment = computed((): PawnEquipment | null => {
  const ids = Array.from(selectedIds.value);
  if (ids.length !== 1) {
    return null;
  }
  const pawnId = ids[0];
  if (pawnId === undefined) {
    return null;
  }
  return pawnEquipment.value.get(pawnId) ?? createEmptyEquipment();
});

// Get inventory for selected pawn
export const selectedPawnInventory = computed((): (ItemData | null)[] | null => {
  const ids = Array.from(selectedIds.value);
  if (ids.length !== 1) {
    return null;
  }
  const pawnId = ids[0];
  if (pawnId === undefined) {
    return null;
  }
  return pawnInventory.value.get(pawnId) ?? createEmptyInventory();
});

// Get combat stance for selected pawn
export const selectedPawnCombatStance = computed((): CombatStanceType => {
  const ids = Array.from(selectedIds.value);
  if (ids.length !== 1) {
    return CombatStance.BALANCED;
  }
  const pawnId = ids[0];
  if (pawnId === undefined) {
    return CombatStance.BALANCED;
  }
  return pawnCombatStance.value.get(pawnId) ?? CombatStance.BALANCED;
});

// Actions
export const setEquipment = ({
  pawnId,
  equipment,
}: {
  pawnId: number;
  equipment: PawnEquipment;
}): void => {
  const next = new Map(pawnEquipment.value);
  next.set(pawnId, equipment);
  pawnEquipment.value = next;
};

export const equipItem = ({
  pawnId,
  slot,
  item,
}: {
  pawnId: number;
  slot: EquipmentSlotType;
  item: ItemData | null;
}): void => {
  const current = pawnEquipment.value.get(pawnId) ?? createEmptyEquipment();
  const next = new Map(pawnEquipment.value);
  next.set(pawnId, { ...current, [slot]: item });
  pawnEquipment.value = next;
};

export const setInventory = ({
  pawnId,
  inventory,
}: {
  pawnId: number;
  inventory: (ItemData | null)[];
}): void => {
  const next = new Map(pawnInventory.value);
  next.set(pawnId, inventory);
  pawnInventory.value = next;
};

export const setInventorySlot = ({
  pawnId,
  slotIndex,
  item,
}: {
  pawnId: number;
  slotIndex: number;
  item: ItemData | null;
}): void => {
  const current = pawnInventory.value.get(pawnId) ?? createEmptyInventory();
  const updated = [...current];
  updated[slotIndex] = item;
  const next = new Map(pawnInventory.value);
  next.set(pawnId, updated);
  pawnInventory.value = next;
};

export const setCombatStance = ({
  pawnId,
  stance,
}: {
  pawnId: number;
  stance: CombatStanceType;
}): void => {
  const next = new Map(pawnCombatStance.value);
  next.set(pawnId, stance);
  pawnCombatStance.value = next;
};

// Initialize pawn with empty gear (call when pawn is created)
export const initializePawnGear = ({ pawnId }: { pawnId: number }): void => {
  const equipNext = new Map(pawnEquipment.value);
  const invNext = new Map(pawnInventory.value);
  const stanceNext = new Map(pawnCombatStance.value);

  if (!equipNext.has(pawnId)) {
    equipNext.set(pawnId, createEmptyEquipment());
  }
  if (!invNext.has(pawnId)) {
    invNext.set(pawnId, createEmptyInventory());
  }
  if (!stanceNext.has(pawnId)) {
    stanceNext.set(pawnId, CombatStance.BALANCED);
  }

  pawnEquipment.value = equipNext;
  pawnInventory.value = invNext;
  pawnCombatStance.value = stanceNext;
};

// Clean up pawn gear when removed
export const removePawnGear = ({ pawnId }: { pawnId: number }): void => {
  const equipNext = new Map(pawnEquipment.value);
  const invNext = new Map(pawnInventory.value);
  const stanceNext = new Map(pawnCombatStance.value);

  equipNext.delete(pawnId);
  invNext.delete(pawnId);
  stanceNext.delete(pawnId);

  pawnEquipment.value = equipNext;
  pawnInventory.value = invNext;
  pawnCombatStance.value = stanceNext;
};

// Mock data for testing
export const createMockItem = ({
  id,
  name,
  rarity = ItemRarity.COMMON,
  slot,
}: {
  id: number;
  name: string;
  rarity?: ItemRarityType;
  slot?: EquipmentSlotType;
}): ItemData => {
  const baseItem = {
    id,
    name,
    icon: name.toLowerCase().replace(/\s+/g, '_'),
    rarity,
    stackable: false,
    quantity: 1,
  };

  if (slot) {
    return {
      ...baseItem,
      slot,
      stats: {
        attack: Math.floor(Math.random() * 20),
        strength: Math.floor(Math.random() * 15),
        defense: Math.floor(Math.random() * 25),
      },
    };
  }

  return baseItem;
};
